import { motion, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Loader3D from '../components/Loader3D';
import { FaArrowRight, FaBookReader, FaPen, FaUsers } from 'react-icons/fa';

function AnimatedHome() {
  const controls = useAnimation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Handle mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const moveX = clientX - window.innerWidth / 2;
      const moveY = clientY - window.innerHeight / 2;
      setMousePosition({ x: moveX, y: moveY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animated features
  const features = [
    { icon: <FaBookReader className="w-8 h-8" />, title: 'Read Stories', description: 'Explore curated content' },
    { icon: <FaPen className="w-8 h-8" />, title: 'Write & Share', description: 'Share your thoughts' },
    { icon: <FaUsers className="w-8 h-8" />, title: 'Join Community', description: 'Connect with others' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-8 md:px-6 lg:px-8"
    >
      {/* Enhanced Background Animation */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 animate-pulse" />
        <motion.div
          className="absolute inset-0"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
            backgroundSize: ['100% 100%', '120% 120%'],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
          }}
        />
        
        {/* Animated grid background */}
        <div className="absolute inset-0 grid grid-cols-8 grid-rows-8">
          {[...Array(64)].map((_, i) => (
            <motion.div
              key={i}
              className="border-[0.5px] border-primary-500/5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.01 }}
            />
          ))}
        </div>
      </div>

      {/* Main Content with Parallax Effect */}
      <motion.div
        className="relative z-10 text-center w-full max-w-7xl mx-auto"
        animate={{
          x: mousePosition.x * 0.02,
          y: mousePosition.y * 0.02,
        }}
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8 relative"
        >
          {/* Animated rings around the loader */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-primary-500/20"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.1, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <Loader3D size="xl" />
        </motion.div>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent"
        >
          Welcome to InkWord
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 md:mb-12 max-w-xl sm:max-w-2xl mx-auto"
        >
          Discover stories, thinking, and expertise from writers on any topic.
        </motion.p>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-12 max-w-4xl mx-auto px-4"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 + index * 0.2 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="p-4 md:p-6 rounded-xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm 
                         border border-gray-200/20 dark:border-gray-700/20"
            >
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="text-primary-500 mb-3 md:mb-4"
              >
                {feature.icon}
              </motion.div>
              <h3 className="text-base md:text-lg font-semibold mb-1 md:mb-2 text-gray-900 dark:text-white">
                {feature.title}
              </h3>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Link to="/" className="w-full sm:w-auto">
            <motion.button
              whileHover={{ scale: 1.05, x: 10 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto px-6 md:px-8 py-2.5 md:py-3 bg-gradient-to-r from-primary-500 to-secondary-500 
                        text-white rounded-lg font-medium shadow-lg hover:shadow-xl 
                        transition-shadow flex items-center justify-center gap-2"
            >
              Start Reading
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FaArrowRight className="inline-block" />
              </motion.span>
            </motion.button>
          </Link>
          <Link to="/about" className="w-full sm:w-auto">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto px-6 md:px-8 py-2.5 md:py-3 bg-white dark:bg-gray-800 
                        text-gray-900 dark:text-white rounded-lg font-medium shadow-lg 
                        hover:shadow-xl transition-all border border-gray-200 dark:border-gray-700"
            >
              Learn More
            </motion.button>
          </Link>
        </motion.div>
      </motion.div>

      {/* Enhanced Floating Elements */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute hidden sm:block"
          animate={{
            x: [0, Math.random() * 100 - 50],
            y: [0, Math.random() * 100 - 50],
            scale: [1, Math.random() + 0.5],
            rotate: [0, Math.random() * 360],
          }}
          transition={{
            duration: Math.random() * 5 + 5,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
          }}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
        >
          <div className={`w-${Math.floor(Math.random() * 8 + 4)} h-${Math.floor(Math.random() * 8 + 4)} 
                        bg-gradient-to-r from-primary-500/20 to-secondary-500/20 
                        rounded-${Math.random() > 0.5 ? 'full' : 'lg'} backdrop-blur-sm`} />
        </motion.div>
      ))}
    </motion.div>
  );
}

export default AnimatedHome; 